// Cloud Run's Google Front End rejects HTTP/1 requests over 32 MiB before
// they reach the app: https://docs.cloud.google.com/run/quotas. Leave headroom
// for the multipart boundaries and per-part headers, which File.size does not
// count.
export const MAX_UPLOAD_BATCH_BYTES = 30 * 1024 * 1024;

/**
 * Groups files, in order, into batches whose combined size stays within
 * `maxBytes`. A single file larger than `maxBytes` gets a batch of its own.
 */
export const batchFilesBySize = (files: File[], maxBytes: number): File[][] => {
  const batches: File[][] = [];
  let current: File[] = [];
  let currentBytes = 0;
  for (const file of files) {
    if (current.length > 0 && currentBytes + file.size > maxBytes) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(file);
    currentBytes += file.size;
  }
  if (current.length > 0) {
    batches.push(current);
  }
  return batches;
};

/** Thrown when a file cannot fit in a single upload request. */
export class FileTooLargeError extends Error {}

/** Throws a `FileTooLargeError` naming every file larger than `maxBytes`. */
export const rejectOversizedFiles = (files: File[], maxBytes: number): void => {
  const oversized = files.filter((f) => f.size > maxBytes).map((f) => f.name);
  if (oversized.length > 0) {
    const limitMb = Math.floor(maxBytes / (1024 * 1024));
    throw new FileTooLargeError(
      `${oversized.join(", ")} ${oversized.length === 1 ? "is" : "are"} ` +
        `larger than the ${limitMb} MB upload limit.`
    );
  }
};
